/**
 * HTML Sanitization Utilities
 * 
 * Provides functions to sanitize HTML content, particularly useful
 * for cleaning pasted content in contentEditable elements.
 */

/**
 * Parses untrusted HTML into an inert fragment.
 *
 * Never use `innerHTML` on an element of the live document for untrusted markup: even a
 * detached `<div>` starts loading `<img src>` and fires `onerror`/`onload` handlers, so
 * `<img src=x onerror=...>` would execute during "sanitization". Template content belongs to an
 * inert document — nothing loads or runs — as long as its nodes are not moved into the page.
 */
function parseInert(html: string): DocumentFragment {
  const template = document.createElement('template');
  template.innerHTML = html;
  return template.content;
}

const EXECUTABLE_TAGS = ['script', 'iframe', 'frame', 'object', 'embed', 'link', 'meta', 'base', 'form', 'style'];
const URL_ATTRIBUTES = ['href', 'src', 'xlink:href', 'action', 'formaction', 'srcset', 'background', 'poster'];

/**
 * Removes executable markup (script-like elements, `on*` handlers, `javascript:`/`vbscript:` URLs)
 * while keeping all formatting. Use before inserting stored, user-authored HTML into the live
 * page (e.g. contentEditable editors), where the allow-list `sanitizeHtml` would strip legitimate
 * formatting that would then be saved back.
 */
export function stripExecutableContent(html: string): string {
  const template = document.createElement('template');
  template.innerHTML = html;

  template.content.querySelectorAll(EXECUTABLE_TAGS.join(',')).forEach((el) => el.remove());

  template.content.querySelectorAll('*').forEach((el) => {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      // Browsers ignore whitespace/control characters inside a scheme ("java\tscript:")
      const value = Array.from(attr.value)
        .filter((ch) => ch.charCodeAt(0) > 0x20)
        .join('')
        .toLowerCase();
      if (
        name.startsWith('on') ||
        (URL_ATTRIBUTES.includes(name) && (value.startsWith('javascript:') || value.startsWith('vbscript:')))
      ) {
        el.removeAttribute(attr.name);
      }
    }
  });

  // Serializes the (still inert) template content
  return template.innerHTML;
}

/**
 * Strip all HTML tags from a string, leaving only plain text
 */
export function stripHtmlTags(html: string): string {
  return parseInert(html).textContent || '';
}

/**
 * Sanitize HTML by allowing only specific safe tags
 * Useful for preserving basic formatting while removing potentially harmful content
 */
export function sanitizeHtml(html: string): string {
  const parsed = parseInert(html);

  // Allowed tags for basic text formatting
  const allowedTags = ['p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'strike', 'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div'];
  
  // Allowed attributes
  const allowedAttributes: Record<string, string[]> = {
    'a': ['href', 'title', 'target'],
    'span': ['style', 'class', 'contenteditable', 'data-variable', 'data-fallback', 'data-label'],
    'p': ['style'],
    'div': ['style'],
  };

  // Allowed style properties (for inline styles)
  const allowedStyles = ['color', 'background-color', 'font-size', 'font-family', 'font-weight', 'text-align', 'text-decoration'];

  function sanitizeNode(node: Node): Node | null {
    // Text nodes are always safe
    if (node.nodeType === Node.TEXT_NODE) {
      return node.cloneNode(true);
    }

    // Only process element nodes
    if (node.nodeType !== Node.ELEMENT_NODE) {
      return null;
    }

    const element = node as Element;
    const tagName = element.tagName.toLowerCase();

    // Check if tag is allowed
    if (!allowedTags.includes(tagName)) {
      // Return text content of disallowed tags
      return document.createTextNode(element.textContent || '');
    }

    // Create sanitized element
    const sanitized = document.createElement(tagName);

    // Copy allowed attributes
    const allowed = allowedAttributes[tagName] || [];
    for (const attr of Array.from(element.attributes)) {
      if (allowed.includes(attr.name)) {
        if (attr.name === 'style') {
          // Sanitize inline styles
          const sanitizedStyle = sanitizeInlineStyle(attr.value);
          if (sanitizedStyle) {
            sanitized.setAttribute('style', sanitizedStyle);
          }
        } else if (attr.name === 'href') {
          // Only allow http, https, and mailto links
          const href = attr.value;
          if (href.match(/^(https?:|mailto:)/i)) {
            sanitized.setAttribute(attr.name, attr.value);
          }
        } else {
          sanitized.setAttribute(attr.name, attr.value);
        }
      }
    }

    // Recursively sanitize children
    for (const child of Array.from(element.childNodes)) {
      const sanitizedChild = sanitizeNode(child);
      if (sanitizedChild) {
        sanitized.appendChild(sanitizedChild);
      }
    }

    return sanitized;
  }

  function sanitizeInlineStyle(styleString: string): string {
    const styles = styleString.split(';').filter(s => s.trim());
    const sanitizedStyles: string[] = [];

    for (const style of styles) {
      const [property, value] = style.split(':').map(s => s.trim());
      if (property && value && allowedStyles.includes(property.toLowerCase())) {
        sanitizedStyles.push(`${property}: ${value}`);
      }
    }

    return sanitizedStyles.join('; ');
  }

  // sanitizeNode builds fresh elements (allow-listed tags/attributes only), so nothing from the
  // inert fragment is ever adopted into the live document.
  const fragment = document.createDocumentFragment();
  for (const child of Array.from(parsed.childNodes)) {
    const sanitized = sanitizeNode(child);
    if (sanitized) {
      fragment.appendChild(sanitized);
    }
  }

  const result = document.createElement('div');
  result.appendChild(fragment);
  return result.innerHTML;
}

/**
 * Clean pasted content to remove formatting and styles from external sources
 * Preserves basic text structure (paragraphs, line breaks) but removes everything else
 */
export function cleanPastedContent(html: string): string {
  // First pass: sanitize to remove dangerous content
  const sanitized = sanitizeHtml(html);
  
  // Second pass: normalize whitespace and structure (inert parse, then serialize)
  const template = document.createElement('template');
  template.innerHTML = sanitized;
  const div = template.content;

  // Remove empty elements
  const empties = div.querySelectorAll('*:empty:not(br)');
  empties.forEach(el => el.remove());

  // Normalize nested paragraphs
  const paragraphs = div.querySelectorAll('p');
  paragraphs.forEach(p => {
    const nestedPs = p.querySelectorAll('p');
    nestedPs.forEach(nested => {
      const br = document.createElement('br');
      nested.replaceWith(br, ...Array.from(nested.childNodes));
    });
  });

  const out = document.createElement('div');
  out.appendChild(div);
  return out.innerHTML;
}

/**
 * Handle paste event in contentEditable element
 * Extracts plain or HTML content from clipboard and sanitizes it
 */
export function handlePaste(event: ClipboardEvent): string | null {
  event.preventDefault();

  const clipboardData = event.clipboardData;
  if (!clipboardData) return null;

  // Try to get HTML first
  const html = clipboardData.getData('text/html');
  
  if (html) {
    // Clean the HTML to remove unwanted formatting
    return cleanPastedContent(html);
  }

  // Fallback to plain text
  const text = clipboardData.getData('text/plain');
  if (text) {
    // Convert plain text to HTML, preserving line breaks
    return text
      .split('\n')
      .map(line => line.trim() ? `<p>${escapeHtml(line)}</p>` : '<br>')
      .join('');
  }

  return null;
}

/**
 * Escape HTML entities to prevent XSS
 */
function escapeHtml(text: string): string {
  const div = document.createElement('div');
  div.textContent = text;
  return div.innerHTML;
}
